import { cx } from "@shared/components/untitledui/util/cx"
import type { Course, CourseDateAnnotationSingle } from "@shared/models/course"
import {
  noteFieldOrNull,
  type NoteWords,
} from "@shared/unified-tiptap/extensions/date-annotation-note/date-annotation-note-extension"
import NoteTiptap from "@shared/unified-tiptap/NoteTiptap"
import type { DerivedPermissions } from "@shared/unified-tiptap/unified-tiptap-ui-context"
import { tracked } from "@supergrain/kernel/react"
import type { Editor } from "@tiptap/core"
import styles from "cc-frontend/components/planner/card-stacks/course-date-annotation.module.css"
import courseDateAnnotationDelete from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_DELETE"
import courseDateAnnotationUpdate from "cc-frontend/lib/actions/course/date/COURSE_DATE_ANNOTATION_UPDATE"
import type Filestack from "cc-frontend/services/filestack"
import { cloneDeep } from "lodash-es"
import { useMemo, useRef } from "react"
import { useDispatch } from "react-app/hooks/useDispatch"
import { useEmberService } from "react-app/providers/ember-service-context"

interface Props {
  annotation: CourseDateAnnotationSingle
  course: Course
  location?: "onMonth"
}

export default tracked(function CourseDateAnnotation({ annotation, course, location }: Props) {
  const dispatch = useDispatch()
  const canEdit = (course.meta.permissions ?? []).includes("can-edit-plan")
  const filestack = useEmberService<Filestack>("filestack")

  const editorRef = useRef<Editor | null>(null)

  const permissions: DerivedPermissions = useMemo(() => ({ canEdit, canComment: false }), [canEdit])

  const updateNote = (words: NoteWords) => {
    const { title, description } = annotation.attributes
    if (
      words.title === noteFieldOrNull(title) &&
      words.description === noteFieldOrNull(description)
    ) {
      return
    }

    const next = cloneDeep(annotation)
    next.attributes = { ...next.attributes, ...words }
    dispatch(courseDateAnnotationUpdate({ annotation: next, course }))
  }

  const deleteNote = () => {
    dispatch(courseDateAnnotationDelete({ course, annotation, calledFrom: "note" }))
  }

  return (
    <div
      className={cx(
        location === "onMonth" && styles["on-month"],
        annotation.attributes.isOff && styles["is-off"],
      )}
      data-test-course-annotation-container=""
      data-test-course-annotation-desc=""
    >
      <NoteTiptap
        permissions={permissions}
        annotationId={annotation.id}
        color={course.attributes.color}
        dateString={annotation.attributes.dateString}
        initialTitle={annotation.attributes.title ?? ""}
        initialDescription={annotation.attributes.description ?? ""}
        onCommit={updateNote}
        onDeleteNote={deleteNote}
        editorRef={editorRef}
        filestackClient={filestack.client}
      />
    </div>
  )
})
